/**
 * Який шар відкрито: палітра `Ctrl+K` чи шпаргалка `?` (UX-10, UX-12). Окреме сховище, а не стан
 * компонента: відкривають їх клавіші, поле пошуку верхньої панелі, меню користувача й самі шари
 * (команда «Шпаргалка клавіш» у палітрі). Фокус після закриття повертається туди, звідки відкрили
 * перший шар, навіть якщо між шарами перемикались (keyboard.md, розд. 6).
 */
import { useSyncExternalStore } from 'react';

export type Overlay = null | { kind: 'palette'; query: string } | { kind: 'cheatsheet' };

let overlay: Overlay = null;
let returnTo: HTMLElement | null = null;
/** Куди повертаємо фокус після закриття — до наступного кадру, поки модальний шар ще в DOM. */
let restoring: HTMLElement | null = null;
const listeners = new Set<() => void>();

const nextFrame = (fn: () => void) =>
  typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 16);

function set(next: Overlay) {
  if (!overlay && next) {
    const active = document.activeElement;
    returnTo =
      restoring ?? (active instanceof HTMLElement && active !== document.body ? active : null);
  }
  overlay = next;
  for (const l of listeners) l();
}

/**
 * Відкрити палітру. `at` — `timeStamp` події, що її відкрила (клавіша, клік): мітка
 * `palette:start` стоїть на ній, тож замір реакції враховує й обробку події
 * (performance-budget.md, розд. 3.2: `palette:start` → `palette:ready`).
 */
export function openPalette(query = '', at?: number) {
  try {
    performance.mark?.('palette:start', at === undefined ? undefined : { startTime: at });
  } catch {
    performance.mark?.('palette:start');
  }
  set({ kind: 'palette', query });
}

export function openCheatsheet() {
  set({ kind: 'cheatsheet' });
}

export function togglePalette(at?: number) {
  if (overlay?.kind === 'palette') closeOverlay();
  else openPalette('', at);
}

/**
 * Закрити шар і повернути фокус туди, звідки його відкрили. Фокус — наступним кадром: поки
 * модальний `<dialog>` у DOM, решта сторінки інертна й фокуса не прийме. Команда, запущена одразу
 * після закриття, може відкрити інший шар або сама перевести фокус — тоді повернення не потрібне.
 */
export function closeOverlay() {
  const target = returnTo;
  returnTo = null;
  restoring = target;
  set(null);
  nextFrame(() => {
    if (restoring !== target) return;
    restoring = null;
    if (!overlay && target?.isConnected && document.activeElement !== target) target.focus();
  });
}

export const getOverlay = () => overlay;

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useOverlay(): Overlay {
  return useSyncExternalStore(subscribe, getOverlay, getOverlay);
}

const TIP_KEY = 'cl:palette-tip';

/**
 * Відкриття палітри мишею (поле пошуку): після третього — одноразова порада про `Ctrl+K`
 * (UX-12). Повертає `true`, коли саме час показати пораду.
 */
export function countMouseOpen(): boolean {
  try {
    const raw = localStorage.getItem(TIP_KEY);
    if (raw === 'shown') return false;
    const n = Number(raw ?? 0) + 1;
    localStorage.setItem(TIP_KEY, n >= 3 ? 'shown' : String(n));
    return n >= 3;
  } catch {
    return false;
  }
}

/** Для тестів: закрити все без повернення фокуса. */
export function resetOverlay() {
  returnTo = null;
  restoring = null;
  overlay = null;
  for (const l of listeners) l();
}
